import { Controller, useFormContext } from 'react-hook-form'

import { castError } from 'modules/expenses/components/expenses/Detail/_newForm/castError'
import withCustomizationFields, {
  WithCustomizationFieldsProps,
} from 'modules/expenses/components/expenses/Detail/_newForm/withCustomizationFields'
import withFieldPermissions, {
  WithFieldPermissionsProps,
} from 'modules/expenses/components/expenses/Detail/_newForm/withFieldPermissions'
import ProjectFields, { FormProject } from 'modules/expenses/components/expenses/Form/ProjectFields'
import { ProjectWorker } from 'modules/expenses/hooks/useProjects'

const FIELD_NAME = 'projects'

interface Props extends WithFieldPermissionsProps, WithCustomizationFieldsProps {
  projectWorkers: ProjectWorker[]
  loading: boolean
  defaultValue?: FormProject
  dirtyEmployee?: boolean
}

const ProjectField = ({
  projectWorkers,
  loading,
  dirtyEmployee,
  isFieldMandatory,
  isFieldReadOnly,
}: Props) => {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext()
  // NOTE: This component is intentionally subscribed to its own value using watch().
  // The underlying input component from the deprecated design system does not reliably
  // re-render when its `value` prop changes, causing a "stale UI" bug.
  //
  // Calling watch() creates a subscription that forces this wrapper component to
  // re-render on every change, ensuring the UI stays in sync with the form state.
  // This is a targeted workaround for the external library's rendering issue.
  watch(FIELD_NAME)
  return (
    <Controller
      name={FIELD_NAME}
      control={control}
      render={({ field }) => (
        <ProjectFields
          value={{
            projectWorkerId: field.value?.[0]?.projectWorker?.id,
            subprojectId: field.value?.[0]?.subproject?.id,
          }}
          defaultValue={dirtyEmployee ? undefined : field.value?.[0]}
          projectWorkers={projectWorkers}
          loading={loading}
          onChange={(value) => {
            const transformedValue = value
              ? [
                  {
                    projectWorker: { id: value.projectWorkerId },
                    subproject: { id: value.subprojectId },
                  },
                ]
              : []
            field.onChange(transformedValue)
          }}
          mandatory={isFieldMandatory}
          errorMessage={castError(errors?.projects)}
          readonly={isFieldReadOnly}
        />
      )}
    />
  )
}

export default withCustomizationFields(withFieldPermissions(ProjectField, FIELD_NAME))
